import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getGroupEdits, GroupEditResponse, GroupEditSort } from '../../service/GroupEditService';
import { convertStrDateUTCToStrDateGB } from '../../utils/Utils';

// ── Constants ─────────────────────────────────────────────────────────────
const MAX_DAYS  = 90;
const PAGE_SIZE = 20;

// ── Helpers ────────────────────────────────────────────────────────────────

function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  return Math.round((b - a) / 86_400_000);
}

function esc(s?: string | null): string {
  if (!s) return '—';
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatDelta(delta: number): string {
  if (delta === 0) return '—';
  return delta > 0 ? `+${delta}` : `${delta}`;
}

function deltaColor(delta: number): string {
  if (delta > 0) return '#d97706';
  if (delta < 0) return '#2563eb';
  return '#6b7280';
}

// ── Print builder — matches PrintWindow.tsx style ─────────────────────────
// Layout: one full-width subheader row per group, then item rows with aligned columns.

function buildPrintHTML(data: GroupEditResponse[]): string {
  const TH  = 'background:#1e3a8a;color:#fff;padding:5px 7px;font-size:9px;text-align:left;border:1px solid #1e40af;white-space:nowrap;';
  const TH_R = TH + 'text-align:right;';
  const TD  = 'border:1px solid #d1d5db;padding:3px 7px;font-size:9px;vertical-align:middle;';
  const TD_R = TD + 'text-align:right;';
  const GH  = 'background:#dbeafe;padding:5px 7px;font-size:9px;font-weight:bold;color:#1e3a8a;border:1px solid #bfdbfe;';

  // 7 data columns: Part Number | Session | Session Date | Delivery Notes | Expected | Qty Before | Qty After | Change
  const COL_COUNT = 8;

  const rows = data.map((ge) => {
    const notes     = (ge.deliveryNotes ?? []).join(', ') || '—';
    const itemCount = (ge.items ?? []).length;
    const comment   = ge.comment ? ` — "${esc(ge.comment)}"` : '';

    // Full-width group subheader
    const groupRow = `
      <tr>
        <td colspan="${COL_COUNT}" style="${GH}">
          ${esc(convertStrDateUTCToStrDateGB(ge.createdAt))}
          &nbsp;·&nbsp;<span style="font-weight:normal;">${esc(ge.createdByName)}</span>
          &nbsp;·&nbsp;${itemCount} item${itemCount !== 1 ? 's' : ''}
          &nbsp;·&nbsp;<span style="color:#0369a1;">${esc(notes)}</span>
          ${comment ? `<span style="font-weight:normal;color:#374151;">${comment}</span>` : ''}
        </td>
      </tr>`;

    const itemRows = (ge.items ?? []).map((it, ii) => {
      const delta   = it.qtyAfter - it.qtyBefore;
      const dColor  = deltaColor(delta);
      const itNotes = (it.deliveryNotes ?? []).join(', ') || '—';
      const sesDate = it.sessionDate ? esc(convertStrDateUTCToStrDateGB(it.sessionDate)) : '—';
      const bg      = ii % 2 === 1 ? 'background:#f9fafb;' : 'background:#fff;';
      return `
      <tr style="${bg}">
        <td style="${TD}font-family:monospace;">${esc(it.partNumber)}</td>
        <td style="${TD}">${esc(it.sessionTitle)}</td>
        <td style="${TD}color:#6b7280;">${sesDate}</td>
        <td style="${TD}">${esc(itNotes)}</td>
        <td style="${TD_R}color:#6b7280;">${it.shipQtd}</td>
        <td style="${TD_R}color:#9ca3af;text-decoration:line-through;">${it.qtyBefore}</td>
        <td style="${TD_R}font-weight:bold;">${it.qtyAfter}</td>
        <td style="${TD_R}font-weight:bold;color:${dColor};">${formatDelta(delta)}</td>
      </tr>`;
    }).join('');

    return groupRow + itemRows;
  }).join('');

  const now = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date());

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Group Edit History Report</title>
  <style>
    @page { size: landscape; margin: 12mm; }
    @media print {
      body { margin: 0; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    body  { font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #1a1a1a; margin: 0; }
    table { width: 100%; border-collapse: collapse; }
    thead { display: table-header-group; }
    tr    { page-break-inside: avoid; }
  </style>
</head>
<body>
<div style="border-bottom:2px solid #2563eb;padding-bottom:8px;margin-bottom:12px;">
  <p style="font-size:16px;font-weight:bold;color:#1e3a8a;margin:0 0 2px 0;">Group Edit History Report</p>
  <p style="font-size:8px;color:#9ca3af;margin:0;">Generated: ${now}</p>
</div>
<table>
  <thead>
    <tr>
      <th style="${TH}">Part Number</th>
      <th style="${TH}">Session</th>
      <th style="${TH}">Session Date</th>
      <th style="${TH}">Delivery Notes</th>
      <th style="${TH_R}">Expected</th>
      <th style="${TH_R}">Qty Before</th>
      <th style="${TH_R}">Qty After</th>
      <th style="${TH_R}">Change</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
</body>
</html>`;
}

// ── Tag input ──────────────────────────────────────────────────────────────

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

const TagInput: React.FC<TagInputProps> = ({ tags, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTags = (raw: string) => {
    const newTags = raw.split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0 && !tags.includes(t));
    if (newTags.length > 0) onChange([...tags, ...newTags]);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTags(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const removeTag = (idx: number) => onChange(tags.filter((_, i) => i !== idx));

  return (
    <div
      className="flex flex-wrap gap-1 items-center border rounded-lg px-2 py-1.5 min-h-[38px] cursor-text focus-within:ring-2 focus-within:ring-blue-300"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, idx) => (
        <span key={idx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs rounded px-2 py-0.5">
          {tag}
          <button type="button" onClick={e => { e.stopPropagation(); removeTag(idx); }}
            className="hover:text-blue-900 font-bold leading-none">×</button>
        </span>
      ))}
      <div className="flex items-center gap-1 flex-1 min-w-[120px]">
        <input
          ref={inputRef}
          type="text"
          className="flex-1 text-sm outline-none bg-transparent min-w-[80px]"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputValue.trim()) addTags(inputValue); }}
          placeholder={tags.length === 0 ? placeholder : ''}
        />
        {inputValue.trim() && (
          <button type="button" onClick={() => addTags(inputValue)}
            className="text-blue-500 hover:text-blue-700 text-sm font-bold px-1" title="Add tag">+</button>
        )}
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

interface Props {
  expandGroupEditId?: string;
}

const GroupEditHistoryTab: React.FC<Props> = ({ expandGroupEditId }) => {
  const [fromDate, setFromDate]       = useState(daysAgo(30));
  const [toDate, setToDate]           = useState(today());
  const [partNumber, setPartNumber]   = useState('');
  const [noteFilters, setNoteFilters] = useState<string[]>([]);
  const [sort, setSort]               = useState<GroupEditSort>('DATE_DESC');

  // Active filter values (committed on "Apply")
  const activeFilters = useRef({ fromDate: daysAgo(30), toDate: today(), partNumber: '', notes: [] as string[], sort: 'DATE_DESC' as GroupEditSort });

  const [data, setData]         = useState<GroupEditResponse[]>([]);
  const [page, setPage]         = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [totalCount, setTotal]  = useState(0);
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(expandGroupEditId ?? null);

  const expandRef      = useRef<HTMLDivElement | null>(null);
  const sentinelRef    = useRef<HTMLDivElement | null>(null);
  const printFrameRef  = useRef<HTMLIFrameElement | null>(null);
  // Track all loaded data for printing (not just current page)
  const allDataRef = useRef<GroupEditResponse[]>([]);

  // ── Fetch a specific page ─────────────────────────────────────────────────
  const fetchPage = useCallback(async (
    fd: string, td: string, pn: string, notes: string[], sortVal: GroupEditSort,
    pageNum: number, append: boolean
  ) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const result = await getGroupEdits({
        fromDate: fd || undefined,
        toDate: td || undefined,
        partNumber: pn.trim() || undefined,
        deliveryNotes: notes.length > 0 ? notes : undefined,
        sort: sortVal,
        page: pageNum,
        size: PAGE_SIZE,
      });
      setData(prev => {
        const next = append ? [...prev, ...result.content] : result.content;
        allDataRef.current = next;
        return next;
      });
      setPage(pageNum);
      setHasMore(result.hasMore);
      setTotal(result.totalCount);
    } catch {
      setError('Failed to load group edit history.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchPage(fromDate, toDate, partNumber, noteFilters, sort, 0, false);
  }, []); // eslint-disable-line

  // ── Auto-expand scroll ────────────────────────────────────────────────────
  useEffect(() => {
    if (expandGroupEditId && expandRef.current) {
      expandRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [expandGroupEditId, data]);

  // ── Infinite scroll sentinel ──────────────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const f = activeFilters.current;
          fetchPage(f.fromDate, f.toDate, f.partNumber, f.notes, f.sort, page + 1, true);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, fetchPage]);

  // ── Apply filters (reset to page 0) ──────────────────────────────────────
  const handleApply = () => {
    if (fromDate && toDate) {
      const diff = daysBetween(fromDate, toDate);
      if (diff > MAX_DAYS) {
        setError(`Date range cannot exceed ${MAX_DAYS} days. Current range: ${diff} days.`);
        return;
      }
      if (diff < 0) {
        setError('From date must be before To date.');
        return;
      }
    }
    activeFilters.current = { fromDate, toDate, partNumber, notes: noteFilters, sort };
    allDataRef.current = [];
    fetchPage(fromDate, toDate, partNumber, noteFilters, sort, 0, false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleApply();
  };

  // ── Print — uses all loaded data ──────────────────────────────────────────
  const handlePrint = () => {
    const iframe = printFrameRef.current;
    if (!iframe?.contentWindow) return;
    const pri = iframe.contentWindow;
    pri.document.open();
    pri.document.write(buildPrintHTML(allDataRef.current));
    pri.document.close();
    pri.focus();
    pri.print();
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
      {/* Hidden print iframe */}
      <iframe ref={printFrameRef} style={{ height: 0, width: 0, position: 'absolute', border: 'none' }} title="group-edit-print" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Group Edit History</h2>
        {data.length > 0 && (
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition"
          >
            <i className="fas fa-print"></i> Print
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            From Date <span className="text-gray-400">(max {MAX_DAYS} days)</span>
          </label>
          <input type="date"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={fromDate} onChange={e => setFromDate(e.target.value)} onKeyDown={handleKeyDown} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
          <input type="date"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={toDate} onChange={e => setToDate(e.target.value)} onKeyDown={handleKeyDown} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Part Number</label>
          <input type="text"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            placeholder="Contains…" value={partNumber}
            onChange={e => setPartNumber(e.target.value)} onKeyDown={handleKeyDown} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Delivery Notes <span className="text-gray-400 font-normal">(Enter, + or comma)</span>
          </label>
          <TagInput tags={noteFilters} onChange={setNoteFilters} placeholder="Add delivery note…" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sort By</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            value={sort}
            onChange={e => setSort(e.target.value as GroupEditSort)}
          >
            <option value="DATE_DESC">Date (newest first)</option>
            <option value="DATE_ASC">Date (oldest first)</option>
            <option value="PART_ASC">Part Number (A → Z)</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">
          {!loading && totalCount > 0 && (
            <>Showing <strong>{data.length}</strong> of <strong>{totalCount}</strong> group edit{totalCount !== 1 ? 's' : ''}</>
          )}
        </p>
        <button onClick={handleApply} disabled={loading}
          className="gradient-bg text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-60">
          {loading ? (
            <span className="flex items-center gap-2">
              <i className="fas fa-circle-notch fa-spin"></i> Loading…
            </span>
          ) : 'Apply Filters'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600"><i className="fas fa-exclamation-circle mr-1"></i>{error}</p>}

      {/* Initial loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <i className="fas fa-circle-notch fa-spin text-2xl mr-3"></i>
          <span className="text-sm">Loading group edits…</span>
        </div>
      )}

      {!loading && data.length === 0 && !error && (
        <p className="text-gray-400 text-sm py-6 text-center">
          No group edits found for the selected filters.
        </p>
      )}

      {/* Cards */}
      {!loading && (
        <div className="space-y-3">
          {data.map(ge => {
            const isExpanded   = expandedId === ge.groupEditId;
            const isAutoExpand = expandGroupEditId === ge.groupEditId;
            const notes        = (ge.deliveryNotes ?? []).join(', ');

            return (
              <div
                key={ge.groupEditId}
                ref={isAutoExpand ? expandRef : undefined}
                className={`border rounded-xl overflow-hidden transition-shadow ${isAutoExpand ? 'ring-2 ring-blue-400' : ''}`}
              >
                {/* Summary row */}
                <div
                  className="flex flex-wrap items-center gap-3 px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                  onClick={() => setExpandedId(isExpanded ? null : ge.groupEditId)}
                >
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {convertStrDateUTCToStrDateGB(ge.createdAt)}
                  </span>
                  <span className="font-medium text-sm text-gray-800">{ge.createdByName}</span>
                  <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-medium whitespace-nowrap">
                    {(ge.items ?? []).length} item{(ge.items ?? []).length !== 1 ? 's' : ''}
                  </span>
                  {notes && (
                    <span className="text-xs text-gray-500 truncate max-w-xs" title={notes}>
                      <i className="fas fa-truck mr-1 text-gray-400"></i>{notes}
                    </span>
                  )}
                  {ge.comment && (
                    <span className="text-sm text-gray-500 italic truncate flex-1">"{ge.comment}"</span>
                  )}
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-xs text-gray-400 ml-auto`}></i>
                </div>

                {/* Expanded detail table */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs font-semibold text-gray-500 uppercase tracking-wide bg-white">
                          <th className="px-4 py-2">Part Number</th>
                          <th className="px-4 py-2">Session</th>
                          <th className="px-4 py-2">Session Date</th>
                          <th className="px-4 py-2">Delivery Notes</th>
                          <th className="px-4 py-2 text-right">Expected</th>
                          <th className="px-4 py-2 text-right">Qty Before</th>
                          <th className="px-4 py-2 text-right">Qty After</th>
                          <th className="px-4 py-2 text-right">Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ge.items ?? []).map(item => {
                          const delta     = item.qtyAfter - item.qtyBefore;
                          const itemNotes = (item.deliveryNotes ?? []).join(', ') || '—';
                          const sesDate   = item.sessionDate
                            ? convertStrDateUTCToStrDateGB(item.sessionDate)
                            : '—';
                          return (
                            <tr key={item.itemId} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-mono text-xs">{item.partNumber}</td>
                              <td className="px-4 py-2 text-xs">
                                <span className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">
                                  {item.sessionTitle}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">{sesDate}</td>
                              <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate" title={itemNotes}>
                                {itemNotes}
                              </td>
                              <td className="px-4 py-2 text-right text-gray-400">{item.shipQtd}</td>
                              <td className="px-4 py-2 text-right text-gray-400 line-through">{item.qtyBefore}</td>
                              <td className="px-4 py-2 text-right font-semibold">{item.qtyAfter}</td>
                              <td className="px-4 py-2 text-right text-xs font-semibold">
                                {delta === 0 ? (
                                  <span className="text-gray-400">—</span>
                                ) : delta > 0 ? (
                                  <span className="text-amber-600">+{delta}</span>
                                ) : (
                                  <span className="text-blue-600">{delta}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-4" />

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="flex items-center justify-center py-4 text-gray-400">
          <i className="fas fa-circle-notch fa-spin mr-2"></i>
          <span className="text-sm">Loading more…</span>
        </div>
      )}

      {/* End of results */}
      {!loading && !loadingMore && !hasMore && data.length > 0 && (
        <p className="text-center text-xs text-gray-400 py-2">
          All {totalCount} group edit{totalCount !== 1 ? 's' : ''} loaded
        </p>
      )}
    </div>
  );
};

export default GroupEditHistoryTab;
